import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  BriefcaseIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const ClientPortal = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    project_title: '',
    impact_statement: '',
    description: '',
    stakeholders: [''],
    budget: '',
    timeline: '',
    project_type: '',
  });

  const createProjectMutation = useMutation({
    mutationFn: (data) => api.post('/api/projects', data),
    onSuccess: (response) => {
      toast.success('Project submitted successfully!');
      navigate(`/projects/${response.data.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to submit project');
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStakeholderChange = (index, value) => {
    const newStakeholders = [...formData.stakeholders];
    newStakeholders[index] = value;
    setFormData((prev) => ({ ...prev, stakeholders: newStakeholders }));
  };

  const addStakeholder = () => {
    setFormData((prev) => ({
      ...prev,
      stakeholders: [...prev.stakeholders, ''],
    }));
  };

  const removeStakeholder = (index) => {
    const newStakeholders = formData.stakeholders.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, stakeholders: newStakeholders }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!formData.project_title.trim()) {
      toast.error('Project title is required');
      return;
    }
    if (!formData.impact_statement.trim()) {
      toast.error('Impact statement is required');
      return;
    }

    // Filter out empty stakeholders
    const validStakeholders = formData.stakeholders.filter((s) => s.trim());
    if (validStakeholders.length === 0) {
      toast.error('At least one stakeholder is required');
      return;
    }

    createProjectMutation.mutate({
      name: formData.project_title,
      impact_statement: formData.impact_statement,
      description: formData.description,
      stakeholders: validStakeholders,
      budget: formData.budget ? parseFloat(formData.budget) : null,
      timeline: formData.timeline || null,
      project_type: formData.project_type || null,
      client_portal: true,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <SparklesIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Submit Your Project
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tell us about your project vision and let our AI-powered platform turn it into reality
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          {/* Project Title */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <BriefcaseIcon className="w-5 h-5 mr-2 text-blue-600" />
              Project Title *
            </label>
            <input
              type="text"
              name="project_title"
              value={formData.project_title}
              onChange={handleInputChange}
              placeholder="e.g., Mobile App for Food Delivery"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Impact Statement */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <SparklesIcon className="w-5 h-5 mr-2 text-purple-600" />
              Impact Statement *
            </label>
            <textarea
              name="impact_statement"
              value={formData.impact_statement}
              onChange={handleInputChange}
              placeholder="What value will this project create? Who will benefit?"
              required
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
            />
            <p className="mt-1 text-sm text-gray-500">
              Describe the business value and expected outcomes
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Additional details about your project..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
            />
          </div>

          {/* Project Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Type
            </label>
            <select
              name="project_type"
              value={formData.project_type}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">Select type...</option>
              <option value="software">Software Development</option>
              <option value="mobile">Mobile App</option>
              <option value="web">Web Application</option>
              <option value="api">API Development</option>
              <option value="infrastructure">Infrastructure</option>
              <option value="data">Data & Analytics</option>
              <option value="ai">AI/ML Project</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Stakeholders */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <UserGroupIcon className="w-5 h-5 mr-2 text-green-600" />
              Stakeholders *
            </label>
            <div className="space-y-3">
              {formData.stakeholders.map((stakeholder, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="email"
                    value={stakeholder}
                    onChange={(e) => handleStakeholderChange(index, e.target.value)}
                    placeholder="stakeholder@email.com"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  />
                  {formData.stakeholders.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStakeholder(index)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addStakeholder}
                className="inline-flex items-center px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Another Stakeholder
              </button>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Who should be notified about project updates?
            </p>
          </div>

          {/* Budget and Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <CurrencyDollarIcon className="w-5 h-5 mr-2 text-yellow-600" />
                Budget (Optional)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  placeholder="50000"
                  min="0"
                  step="1000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeline (Optional)
              </label>
              <input
                type="text"
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                placeholder="e.g., 3 months, Q2 2025"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createProjectMutation.isPending}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {createProjectMutation.isPending ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Project'
              )}
            </button>
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">What happens next?</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <span className="mr-2">1.</span>
              <span>Our AI will analyze your requirements and create an initial project plan</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">2.</span>
              <span>Stakeholders will receive notifications about project milestones</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">3.</span>
              <span>You'll get access to the full project dashboard with real-time updates</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ClientPortal;
